/**
 * THEME-AWARE SURFACES for the panels that hardcoded light fills.
 *
 * <h2>Why these existed as bugs rather than as choices</h2>
 *
 * The Messages Panel pills and the Visual Universe fields were written against a light canvas and
 * set a BACKGROUND without setting a TEXT COLOUR. That is correct and invisible while the only
 * theme is light — the inherited colour is dark, and it reads. The moment the dark theme shipped
 * the inherited colour became light and every one of them went to roughly ONE to one:
 *
 * <pre>
 *   #f8f8f8 field   with the theme's #e6edf3   1.11 : 1
 *   #e5ffe5 pill    "                          1.11 : 1
 *   #ffeaea pill    "                          1.02 : 1
 *   #fff9c4 pill    "                          1.10 : 1
 * </pre>
 *
 * Not "hard to read" — not visible at all. And nothing errored, because a colour is never wrong,
 * only unreadable.
 *
 * <h2>⚠ THEME-AWARE, not "replace the light values with dark ones"</h2>
 *
 * Light mode is a shipped, supported theme with a toggle in the app bar, so a fix that swapped the
 * pastels for dark tints would move the bug rather than remove it — the same panels would be
 * unreadable for anyone who clicks the Sun. Every export here is a function of the MUI theme, read
 * through `sx`'s own theme callback at render time, so the surfaces follow the toggle without the
 * callers knowing a theme exists.
 *
 * <h2>The fills cannot carry the edge, so the borders do</h2>
 *
 * Measured against the dark dialog's own `#161b22`, a tint at the specified alpha is
 * <b>1.16 to 1.50 : 1</b> — far under the 3:1 floor a graphical object needs to register as a
 * region. That is the same arithmetic recorded for the PTA cycle tint at 0.035: a low-alpha fill
 * over a flat surface reads as the surface. So each pill's BORDER is what makes it a pill, and
 * each border alpha is the smallest that clears 3:1 rather than a value chosen by eye.
 */

/** The pastel fills these panels have always used in light mode. Unchanged, deliberately. */
const LIGHT = {
  positive: { fill: '#e5ffe5', border: '#e0e0e0' },
  negative: { fill: '#ffeaea', border: '#e0e0e0' },
  highlight: { fill: '#fff9c4', border: '#f9a825', gradient: 'linear-gradient(90deg, #fff9c4 0%, #fff176 100%)' },
};

/**
 * The dark tints, and the measurements behind each one.
 *
 * <p>Hues are the ones asked for — Tailwind green-500, red-500 and yellow-500 — at the specified
 * fill alphas. The BORDER alphas are solved rather than specified: 0.45 across the board, which is
 * the obvious choice, leaves red at 1.83:1 against the dialog and invisible. These are the
 * smallest alphas per hue that reach 3:1.
 *
 * | | fill on #161b22 | vs text #e6edf3 | border | vs #161b22 |
 * |---|---|---|---|---|
 * | green  | #18342b | 11.36 | 0.55 | 3.15 |
 * | red    | #372127 | 12.61 | 0.75 | 3.08 |
 * | yellow | #40391d |  9.76 | 0.50 | 3.19 |
 */
const DARK = {
  positive: { fill: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.55)' },
  negative: { fill: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.75)' },
  highlight: {
    fill: 'rgba(234, 179, 8, 0.20)',
    border: 'rgba(234, 179, 8, 0.50)',
    // A gradient, as in light mode, so the highlighted row is distinguished by the same device in
    // both themes rather than by two different ones.
    gradient: 'linear-gradient(90deg, rgba(234,179,8,0.20) 0%, rgba(234,179,8,0.34) 100%)',
  },
};

/** The tint set for the active mode. */
export function summaryTints(theme) {
  return theme.palette.mode === 'dark' ? DARK : LIGHT;
}

/**
 * One row of the Messages Panel summary list.
 *
 * <p>Pass this straight to `sx` — it is the theme callback, so a caller needs no theme of its own.
 *
 * @param variant 'positive' | 'negative' | 'highlight'
 */
export const summaryRowSx = (variant) => (theme) => {
  const t = summaryTints(theme)[variant] || summaryTints(theme).positive;
  const highlight = variant === 'highlight';
  return {
    display: 'flex',
    justifyContent: 'space-between',
    px: 2,
    py: 1,
    fontFamily: 'monospace',
    fontSize: 16,
    borderRadius: 2,
    m: 0.5,
    background: t.gradient || t.fill,
    border: `${highlight ? 2 : 1}px solid ${t.border}`,
    /*
     * ⚠ THE WHOLE POINT. Stated rather than inherited, in BOTH modes.
     *
     * Inheriting is what broke this: the pills carried a light fill and took whatever colour the
     * page happened to set, which was dark until it wasn't. Naming it here means the pair is
     * decided in one place and cannot come apart again — and `text.primary` rather than a literal,
     * so it is the same colour the rest of the dialog uses and follows any later palette change.
     */
    color: theme.palette.text.primary,
  };
};

/**
 * A READ-ONLY TextField on a dark dialog — the Visual Universe's date and status fields.
 *
 * <h2>Why not simply delete the hardcoded background</h2>
 *
 * Removing `background: '#f8f8f8'` does fix the contrast outright: the field falls back to MUI's
 * outlined styling, which is already theme-aware, and reads at 14.6:1. It also stops looking like
 * a field — an outlined box with no fill on a dialog of the same colour reads as a label with a
 * rectangle round it. These are value displays sitting next to a coloured button, so they keep a
 * lifted surface; it is the LIFT that is now theme-aware rather than a fixed near-white.
 *
 * <h2>⚠ The specified `#334155` border is invisible here</h2>
 *
 * Against the lifted `#21262d` it measures <b>1.47 : 1</b>. Two dark greys cannot separate by fill,
 * so on a dark dialog a field is defined by its outline and that outline has to be visible:
 * `rgba(255,255,255,0.35)` is the smallest that clears 3:1 (3.15 against the field, 3.58 against
 * the dialog). The surface itself is `#21262d` rather than the proposed `#1E293B` only because
 * this theme is GitHub-dark rather than Tailwind-slate, and one blue-cast panel among the rest
 * would read as a different application.
 *
 * <p>Light mode keeps `#f8f8f8` exactly as before.
 */
export const readOnlyFieldSx = (theme) => {
  const dark = theme.palette.mode === 'dark';
  return {
    borderRadius: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: dark ? '#21262d' : '#f8f8f8',
      /*
       * ⚠ ON THE SHELL, not on `.MuiInputBase-input`.
       *
       * MUI's own input rule is `color: currentColor`, and it outranks a descendant selector from
       * the `sx` root -- so a colour set there is overridden and the computed value stays
       * `currentColor`. Setting it here is what `currentColor` then resolves to.
       *
       * <p>Belt and braces rather than the fix: MUI already colours this from `text.primary`, so
       * the VALUE was never the wrong colour. It was the right colour on a hardcoded near-white
       * background, which is why it vanished. Stated anyway, so the pair is decided in one place.
       */
      color: theme.palette.text.primary,
      '& fieldset': { borderColor: dark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.23)' },
      // Read-only, so there is no focus ring to speak of -- but a hover that BRIGHTENS would
      // suggest it can be typed into. Held steady instead.
      '&:hover fieldset': { borderColor: dark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.23)' },
    },
    // The label sits ON the dialog surface, not on the field, so it is measured against that:
    // text.secondary is 6.2:1 there. It was unreadable before only because nothing set it and
    // MUI's default label colour is tuned for the opposite canvas.
    '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
    '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
  };
};

/**
 * A panel floated over the chart — the Visual Universe's aspect list.
 *
 * <p>Near-white at 0.96 alpha, which on a dark canvas is a white card with light text on it. The
 * same defect as the fields and not separately reported, almost certainly because the panel is
 * behind a toggle that was off.
 */
export const floatingPanelSx = (theme) => {
  const dark = theme.palette.mode === 'dark';
  return {
    bgcolor: dark ? 'rgba(22, 27, 34, 0.96)' : 'rgba(255, 255, 255, 0.96)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(0, 0, 0, 0.2)'}`,
    color: theme.palette.text.primary,
  };
};

/** The hairline inside that panel, in whichever direction the surface runs. */
export const panelDividerColor = (theme) => (
  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.12)'
);

/**
 * A FORM ROW inside a study designer — the bordered strips the MOA tabs and the Strategy
 * Designer build their pickers on.
 *
 * <h2>⚠ THE SAME DEFECT AS THE READ-ONLY FIELDS, IN NINE MORE FILES</h2>
 *
 * Each row set a background and no text colour, so the colour was inherited — correct and
 * invisible while the only theme was light, and on the dark theme the inherited value became
 * `#e6edf3`. Measured on the live dialogs before changing anything:
 *
 * <ul>
 *   <li><b>MOA Strategy Designer</b> — 76 unreadable nodes, `#e6edf3` on `#fafafa`, <b>1.13 : 1</b></li>
 *   <li><b>Design A New Strategy</b> — 42 unreadable nodes, `#e6edf3` on `#ffffff`, <b>1.18 : 1</b></li>
 * </ul>
 *
 * <p>Not "hard to read" — not visible. And nothing errors, because a colour is never wrong, only
 * unreadable. They surfaced now because the four strategy designers reached the astro and
 * dhanabhyas toolbars; the dialogs themselves are older than the dark theme.
 *
 * <h2>⚠ THEME-AWARE, NOT "SWAP THE LIGHT VALUES FOR DARK ONES"</h2>
 *
 * Light mode is a shipped theme with a toggle in the app bar, so replacing `#fafafa` with a dark
 * tint would MOVE the bug rather than remove it — the same rows unreadable for anyone who clicks
 * the Sun. The light branch returns the exact values these rows already had, which is a separate
 * claim from the contrast one and is tested separately: a contrast assertion alone passes against
 * dark tints on a white page.
 *
 * <p>The dark surface is `#21262d` and the border `rgba(255,255,255,0.22)`, matching
 * {@link readOnlyFieldSx} — two dark greys cannot separate by fill (the lift is only ~1.14:1
 * against the dialog), so on this canvas a row is defined by its outline.
 *
 * @param variant `'plain'` for the `#ffffff` rows in the Strategy Designer, `'tinted'` for the
 *   `#fafafa` rows in the MOA tabs. One function rather than two, because the pair has to move
 *   together — they are the same control on two dialogs a click apart.
 */
export const formRowSx = (variant = 'tinted') => (theme) => {
  const dark = theme.palette.mode === 'dark';
  return {
    backgroundColor: dark
      ? '#21262d'
      : (variant === 'plain' ? '#fff' : '#fafafa'),
    /*
     * ⚠ THE HALF THAT WAS MISSING. Everything else here is cosmetic; this is the defect.
     * `text.primary` rather than a literal, so the row follows the theme it is handed instead of
     * carrying a third copy of the two values.
     */
    color: theme.palette.text.primary,
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.22)' : '#d0d0d0'}`,
  };
};
